import React, { useState, useEffect } from 'react';
import TopBar from "../../components/TopBar";
import { Link, useNavigate } from 'react-router-dom';
import { countRows, fetchRecentOrders, listBranches, fetchRecentBids, fetchTotalBids, fetchAuctionsWithBranch, fetchTotalAuctions, fetchRecentBankAccounts, fetchTotalBankAccounts, fetchProducts, fetchAllAuctions } from '../../lib/api';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import { useAuth } from '../../contexts/AuthContext';

function Landing() {
  const navigate = useNavigate();
  const { branchId, isAdmin } = useBranchFilter();
  const { user } = useAuth();
  const [summaryData, setSummaryData] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalBranches: 0,
    totalBids: 0,
    totalAuctions: 0,
    totalBankAccounts: 0,
    recentOrders: [],
    branches: [],
    recentBids: [],
    recentAuctions: [],
    recentBankAccounts: [],
  });

  useEffect(() => {
    (async () => {
      try {
        // For branch managers, filter data by their branch
        let totalProducts, totalAuctions, recentAuctions;
        
        if (branchId) {
          // Branch manager - count only their branch's data
          const products = await fetchProducts({ branch_id: branchId });
          totalProducts = products.length;
          
          const allAuctions = await fetchAllAuctions();
          const branchAuctions = allAuctions.filter(a => a.branch_id === branchId);
          totalAuctions = branchAuctions.length;
          recentAuctions = branchAuctions.slice(0, 8);
        } else {
          // Admin - see all data
          totalProducts = await countRows('products');
          totalAuctions = await fetchTotalAuctions();
          recentAuctions = await fetchAuctionsWithBranch(8);
        }
        
        const [totalOrders, totalUsers, recentOrders, branchList, recentBids, totalBids, recentBankAccounts, totalBankAccounts] = await Promise.all([
          countRows('payments'),
          countRows('user_details'),
          fetchRecentOrders(4),
          isAdmin ? listBranches() : [],
          fetchRecentBids(8),
          fetchTotalBids(),
          fetchRecentBankAccounts(8),
          fetchTotalBankAccounts(),
        ]);
        
        setSummaryData({ 
          totalProducts, 
          totalOrders, 
          totalUsers, 
          totalBranches: branchList.length,
          totalBids,
          totalAuctions,
          totalBankAccounts,
          recentOrders,
          branches: isAdmin ? branchList.slice(0, 6) : [],
          recentBids,
          recentAuctions,
          recentBankAccounts,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load landing stats', e);
      }
    })();
  }, [branchId, isAdmin]);

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar />
      <div className="p-6 pb-8">
        {/* Welcome Message */}
        <div className="mb-8">
          <p className="text-gray-600 text-sm">Welcome back,</p>
          <h2 className="text-2xl font-bold text-gray-900">
            {isAdmin ? 'Admin Dashboard Overview' : `${user?.branch?.name || user?.branch?.branch_name || 'Branch'} Dashboard`}
          </h2>
          {!isAdmin && user?.branch && (
            <p className="text-gray-500 text-sm mt-1">Managing: {user.branch.name || user.branch.branch_name}</p>
          )}
        </div>

        {/* Summary Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${isAdmin ? 'xl:grid-cols-7' : 'xl:grid-cols-6'} gap-4 mb-8`}>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Products</h3>
            <p className="text-3xl font-bold text-blue-600">{summaryData.totalProducts}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Orders</h3>
            <p className="text-3xl font-bold text-green-600">{summaryData.totalOrders}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Users</h3>
            <p className="text-3xl font-bold text-purple-600">{summaryData.totalUsers}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Auctions</h3>
            <p className="text-3xl font-bold text-red-600">{summaryData.totalAuctions}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Bids</h3>
            <p className="text-3xl font-bold text-orange-600">{summaryData.totalBids}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Accounts</h3>
            <p className="text-3xl font-bold text-teal-600">{summaryData.totalBankAccounts}</p>
          </div>
          {isAdmin && (
            <div 
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate('/branches')}
            >
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Branches</h3>
              <p className="text-3xl font-bold text-indigo-600">{summaryData.totalBranches}</p>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Recent Orders</h2>
          <ul className="divide-y divide-gray-200">
            {summaryData.recentOrders.map(order => (
              <li key={order.id} className="py-4 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">Order #{order.id}</span>
                  <span className="ml-2 text-gray-600">for {order.customer}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-600 font-semibold mr-4">₹{Number(order.amount || 0).toLocaleString()}</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${order.status?.toLowerCase() === 'delivered' ? 'bg-green-100 text-green-800' : order.status?.toLowerCase() === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {order.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 text-right">
            <Link to="/orders" className="text-sm font-medium text-blue-600 hover:underline">View All Orders</Link>
          </div>
        </div>

        {/* Branches Section - Admin Only */}
        {isAdmin && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-700">Store Branches</h2>
              <button
                onClick={() => navigate('/branches')}
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                View All →
              </button>
            </div>
            {summaryData.branches.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No branches available</p>
                <button
                  onClick={() => navigate('/branches')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Add Your First Branch
                </button>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summaryData.branches.map((branch) => (
                <div
                  key={branch.id}
                  onClick={() => navigate(`/branches/${branch.name || branch.branch_name}`)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <h3 className="font-semibold text-gray-800">{branch.name || branch.branch_name}</h3>
                  </div>
                  {branch.branch_name && branch.branch_name !== branch.name && (
                    <p className="text-sm text-gray-500 ml-6">{branch.branch_name}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2 ml-6">
                    Created: {new Date(branch.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {/* Auctions Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">
              {isAdmin ? 'Auctions by Branch' : 'My Branch Auctions'}
            </h2>
            <span className="text-sm text-gray-500">Latest {summaryData.recentAuctions.length} auctions</span>
          </div>
          {summaryData.recentAuctions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No auctions available yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auction ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auction Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch Name
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summaryData.recentAuctions.map((auction) => (
                    <tr key={auction.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {auction.id ? (typeof auction.id === 'string' ? `${auction.id.substring(0, 8)}...` : auction.id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {auction.auction_date ? new Date(auction.auction_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {auction.branch_id ? (typeof auction.branch_id === 'string' ? `${auction.branch_id.substring(0, 8)}...` : auction.branch_id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          <span className="font-semibold text-gray-900">
                            {auction.branch_name || 'Unknown Branch'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Bids Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Recent Bids</h2>
            <span className="text-sm text-gray-500">Latest {summaryData.recentBids.length} bids</span>
          </div>
          {summaryData.recentBids.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No bids available yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bid ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auction ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bidder
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Placed At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summaryData.recentBids.map((bid) => (
                    <tr key={bid.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {bid.id ? (typeof bid.id === 'string' ? `${bid.id.substring(0, 8)}...` : bid.id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {bid.auction_id ? (typeof bid.auction_id === 'string' ? `${bid.auction_id.substring(0, 8)}...` : bid.auction_id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {bid.auction_product_id ? (typeof bid.auction_product_id === 'string' ? `${bid.auction_product_id.substring(0, 8)}...` : bid.auction_product_id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {bid.bidder ? (typeof bid.bidder === 'string' ? `${bid.bidder.substring(0, 8)}...` : bid.bidder) : 'Anonymous'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-green-600">
                          ₹{Number(bid.amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {bid.is_auto ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                            Auto
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            Manual
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {bid.placed_at ? new Date(bid.placed_at).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* User Bank Accounts Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">User Bank Accounts</h2>
            <span className="text-sm text-gray-500">Latest {summaryData.recentBankAccounts.length} accounts</span>
          </div>
          {summaryData.recentBankAccounts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No bank accounts available yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bank Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IFSC Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Holder
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summaryData.recentBankAccounts.map((account) => (
                    <tr key={account.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {account.id ? (typeof account.id === 'string' ? `${account.id.substring(0, 8)}...` : account.id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {account.user_id ? (typeof account.user_id === 'string' ? `${account.user_id.substring(0, 8)}...` : account.user_id) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-mono text-gray-900">
                          {account.account_number ? `****${String(account.account_number).slice(-4)}` : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {account.bank_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">
                        {account.ifsc_code || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {account.account_holder_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {account.is_verified ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {account.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Landing;
